import { useCallback, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { bridge } from 'shared/src/utils';
import type { GoodsData } from '@banhala/ui-kit';
import type { CodiGoodsItem } from 'shared/src/api';

const SCREEN_NAME = 'GOODS_DETAIL';

type GoodsItem = GoodsData & CodiGoodsItem;

export const useLogDisplayGoods = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const goodsItemLoggedList = useRef<Array<number>>([]);
  const pendingItems = useRef<Array<{ index: number; item: GoodsItem }>>([]);

  useEffect(() => {
    if (enabled && pendingItems.current.length > 0) {
      pendingItems.current.forEach(({ index, item }) => {
        if (goodsItemLoggedList.current.includes(index)) return;
        goodsItemLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          channel: ['amplitude', 'ablylog', 'firebase'],
          name: 'DISPLAY_GOODS',
          analytics: {
            SCREEN: SCREEN_NAME,
            ad: item.item.ad,
            ...item.logging.analytics,
            INDEX: index,
          },
        });
        bridge.logAdEvent({ action: 'display', ad: item.item.ad });
      });
      pendingItems.current = [];
    }
  }, [enabled]);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<GoodsItem>['onViewableItemsChanged']>
  >(({ viewableItems }) => {
    viewableItems.forEach(({ index, isViewable, item }) => {
      if (index === null || !isViewable) {
        return;
      }
      if (goodsItemLoggedList.current.includes(index)) {
        return;
      }

      if (!enabledRef.current) {
        if (!pendingItems.current.some(p => p.index === index)) {
          pendingItems.current.push({ index, item });
        }
        return;
      }

      goodsItemLoggedList.current.push(index);

      bridge.logAnalyticsEvent({
        channel: ['amplitude', 'ablylog', 'firebase'],
        name: 'DISPLAY_GOODS',
        analytics: {
          SCREEN: SCREEN_NAME,
          ad: item.item.ad,
          ...item.logging.analytics,
          INDEX: index,
        },
      });
      bridge.logAdEvent({ action: 'display', ad: item.item.ad });
    });
  }, []);

  return { logDisplayGoods };
};
